import { asElement } from './dom.js';
import { isVirtualPointerDownEvent } from './event.js';
import { getModifiers } from './modifiers.js';
import { EPHEMERAL_CONTAINER_QUERY } from './query.js';
import { HAS_WINDOW } from './window.js';

const POINTER_TYPES = ['mouse', 'touch', 'pen'];
/**
 * The body element, or a mock in SSR contexts.
 * @note this should never be accessed on the server
 */
const BODY_OR_MOCK = HAS_WINDOW ? document.body : {};
// note: the values within `interaction` are all provided such that the type does not change at
// runtime, e.g. a string remains a string, etc. Thus we use the empty string instead of `null`
// for key and code. This lets the browser optimize the object's runtime shape.
const interaction = {
    /**
     * The last element the user interacted with, or the body if no interaction yet.
     */
    element: BODY_OR_MOCK,
    /**
     * The most recent element to receive focus, or the body if nothing has been focused yet.
     * Driven by `focus` events and **never cleared when focus is lost**, so after a focused
     * element is removed from the dom, this still points at it.
     *
     * Removing the focused element triggers the HTML spec's "focus fixup rule",
     * which makes the document viewport the new focused area *without running the
     * focusing steps* — no `focus`/`focusin` is dispatched on the body.
     *
     * @see https://html.spec.whatwg.org/multipage/interaction.html#focus-fixup-rule
     */
    focusedElement: BODY_OR_MOCK,
    /**
     * Whether the last interaction was via keyboard or pointer. Relies on global event
     * listeners updating the last interaction device on pointer and key events.
     *
     * Screen reader clicks are treated as keyboard interactions with a device of 'screen-reader'.
     */
    type: 'pointer',
    stage: 'down',
    /**
     * Last device type that interacted with the page. Can be 'keyboard', 'mouse', 'touch', 'pen',
     * or 'screen-reader' (VO + Space for example).
     */
    device: 'mouse',
    /**
     * A bitmask of the modifier keys that were pressed along with the last interaction. Valid for
     * both keyboard and pointer interactions. The values can be checked against the `Modifier` enum.
     *
     * @example
     *
     * ```ts
     * const shiftKeyPressed = LastInteraction.modifiers & Modifier.SHIFT
     * ```
     */
    modifiers: 0,
    /**
     * The last key that was pressed during a keyboard interaction. Empty string if the last
     * interaction was not via keyboard. This is the `key` property of the KeyboardEvent.
     */
    key: '',
    /**
     * The last code that was pressed during a keyboard interaction. Empty string if the last
     * interaction was not via keyboard. This is the `code` property of the KeyboardEvent.
     */
    code: '',
};
/** Info about the last user action. */
const LastInteraction = interaction;
////////////////////////////////////////////////////////////////////////////////////////////////////
if (HAS_WINDOW) {
    window.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('pointerup', handlePointerUp, true);
    window.addEventListener('keydown', handleKeyboard, true);
    window.addEventListener('keyup', handleKeyboard, true);
    window.addEventListener('focus', handleFocus, true);
}
// Track the number of pointermove events. If this is 2 or greater, then the user is using their
// pointer to move around the window. We use 2 pointermove events because MacOS has a setting
// "Follows VoiceOver Cursor" that will teleport the mouse to the VoiceOver Cursor's position.
// This starts at 2, which means we default to "pointer" mode.
const POINTERMOVE_EVENTS_NEEDED = 2;
let pointerMoveEventCount = POINTERMOVE_EVENTS_NEEDED;
/**
 * Returns if the user is using their keyboard or pointer to move around the browser window.
 */
function getMovementType() {
    return pointerMoveEventCount === POINTERMOVE_EVENTS_NEEDED ? 'pointer' : 'keyboard';
}
// Focus-return fallback ladder, oldest first: the last durable element (bottom, a stable anchor)
// followed by the ephemeral elements (menu items, popover contents, modal descendants, etc.)
// interacted with since. Walked newest-first by `getFocusReturnCandidates`.
const fallbackChain = [];
/**
 * Focus-return candidates for `useFocusAndReturn`, most recent first: the last interacted element,
 * then any ephemeral elements (one per popup) leading back to the last durable element (the final
 * tail). Callers restore focus to the first candidate still connected to the document.
 */
function getFocusReturnCandidates() {
    return [...fallbackChain].reverse();
}
////////////////////////////////////////////////////////////////////////////////////////////////////
function handleMouseDown(e) {
    // Voice over on Safari only sends mousedown/mouseup/click events with VO + Space,
    // so this method is only to handle that specific case.
    if (e.detail === 0) {
        handleVirtualClick(e);
    }
}
function handlePointerDown(e) {
    if (isVirtualPointerDownEvent(e)) {
        handleVirtualClick(e);
        return;
    }
    handlePointer(e);
}
function handlePointerUp(e) {
    // Ignore virtual pointer up events from screen readers
    if (interaction.device === 'screen-reader')
        return;
    handlePointer(e);
}
function handlePointer(e) {
    interaction.type = 'pointer';
    interaction.stage = e.type === 'pointerdown' ? 'down' : 'up';
    interaction.device = getPointerType(e);
    interaction.modifiers = getModifiers(e);
    interaction.key = '';
    interaction.code = '';
    checkTarget(e.target);
}
function handleKeyboard(e) {
    interaction.type = 'keyboard';
    interaction.device = 'keyboard';
    interaction.stage = e.type === 'keydown' ? 'down' : 'up';
    interaction.modifiers = getModifiers(e);
    interaction.key = e.key;
    interaction.code = e.code;
    checkTarget(e.target);
    // When the user inputs with their keyboard, enter "keyboard" mode and listen for mouse moves.
    // addEventListener will automatically dedupe, so we can call this as much as we want.
    pointerMoveEventCount = 0;
    window.addEventListener('pointermove', handlePointerMove, true);
}
function handleVirtualClick(e) {
    interaction.type = 'keyboard';
    interaction.stage = 'down';
    interaction.device = 'screen-reader';
    interaction.modifiers = 0;
    interaction.key = ' ';
    interaction.code = 'Space';
    checkTarget(e.target);
}
function handleFocus(e) {
    const target = asElement(e.target);
    if (target)
        interaction.focusedElement = target;
}
function handlePointerMove() {
    ++pointerMoveEventCount;
    // We can unbind the listener at 2 or more to save on event processing.
    if (pointerMoveEventCount >= POINTERMOVE_EVENTS_NEEDED) {
        window.removeEventListener('pointermove', handlePointerMove, true);
    }
}
// Ephemeral container of the top of `fallbackChain`, or null when the chain is empty. Lets us
// collapse consecutive interactions within the same container (e.g. arrowing through one menu)
// without growing the chain or re-walking the DOM.
let topEphemeralContainer = null;
function checkTarget(el) {
    // Ensure e.target is an Element so callers can make assumptions about what methods
    // are safe to call on it (like .matches()). Fixes a sev2 (2025-02-13):
    // https://figma.enterprise.slack.com/archives/C08CX4GKGPP
    const target = asElement(el) ?? document.body;
    // interaction.element still holds the previous target here (checkTarget is its only writer), so a
    // mismatch means a new element and we re-classify; otherwise nothing changed and we bail.
    if (target !== interaction.element) {
        const container = target.closest(EPHEMERAL_CONTAINER_QUERY);
        if (!container) {
            // A durable element is a stable focus anchor: it becomes the new fallback base, and the
            // ephemeral chain above it is no longer reachable, so drop it.
            fallbackChain.length = 0;
            fallbackChain.push(target);
            topEphemeralContainer = null;
        }
        else if (container === topEphemeralContainer) {
            // Same ephemeral container as the current top (e.g. moving between items in one menu): replace
            // the entry in place so a single popup contributes at most one fallback.
            fallbackChain[fallbackChain.length - 1] = target;
        }
        else {
            // A new ephemeral container: push it as a nearer fallback than everything already recorded.
            fallbackChain.push(target);
            topEphemeralContainer = container;
        }
    }
    interaction.element = target;
}
function getPointerType(e) {
    const pointerType = e.pointerType;
    return POINTER_TYPES.includes(pointerType) ? pointerType : 'mouse';
}

export { LastInteraction, getFocusReturnCandidates, getMovementType };
//# sourceMappingURL=interaction.js.map
